/**
 * Language routing for the public site.
 *
 * English is served from the root (`/about`), every other locale lives under a
 * path prefix (`/es/about`). The prefix is the only thing that makes a language
 * version crawlable: hreflang needs distinct URLs, and a static host cannot
 * serve different HTML per query string.
 */

import { TRANSLATED_PATHS } from './translatedPaths';

export const DEFAULT_LANGUAGE = 'en';

export const LOCALIZED_LANGUAGES = ['zh', 'es', 'pt', 'fr', 'ru', 'ar'] as const;

export type LocalizedLanguage = (typeof LOCALIZED_LANGUAGES)[number];

export const ALL_LANGUAGES: string[] = [DEFAULT_LANGUAGE, ...LOCALIZED_LANGUAGES];

/** BCP-47 tag used for <html lang> and WebPage.inLanguage. */
export const HTML_LANG: Record<string, string> = {
  en: 'en',
  zh: 'zh-CN',
  es: 'es',
  pt: 'pt',
  fr: 'fr',
  ru: 'ru',
  ar: 'ar',
};

/** Open Graph locale codes. */
export const OG_LOCALE: Record<string, string> = {
  en: 'en_US',
  zh: 'zh_CN',
  es: 'es_ES',
  pt: 'pt_BR',
  fr: 'fr_FR',
  ru: 'ru_RU',
  ar: 'ar_AR',
};

export const RTL_LANGUAGES = ['ar'];

const LOCALE_PREFIX = /^\/([a-z]{2})(?=\/|$)/;

/** Locale implied by a pathname; English for anything unprefixed. */
export function localeFromPath(pathname: string): string {
  const match = LOCALE_PREFIX.exec(pathname);
  if (!match) return DEFAULT_LANGUAGE;
  const candidate = match[1];
  return LOCALIZED_LANGUAGES.includes(candidate as LocalizedLanguage) ? candidate : DEFAULT_LANGUAGE;
}

/** `/es/about` → `/about`; `/es` → `/`. */
export function stripLocale(pathname: string): string {
  const locale = localeFromPath(pathname);
  if (locale === DEFAULT_LANGUAGE) return pathname || '/';
  const stripped = pathname.slice(locale.length + 1);
  return stripped === '' ? '/' : stripped;
}

/**
 * True when the path has a translated language version. Pages that exist in
 * English only (buyer's guides, comparisons) must not be prefixed — the URL
 * would 404 and the hreflang signal would be false.
 */
export function hasTranslatedVersion(pathname: string): boolean {
  const base = stripLocale(pathname);
  return TRANSLATED_PATHS.includes(base) || /^\/product\/infrared-heaters\/[a-z0-9-]+$/.test(base);
}

/** Prefix only when a translated version exists; otherwise keep the path as-is. */
export function localizeIfTranslated(language: string, pathname: string): string {
  return hasTranslatedVersion(pathname) ? localizePath(language, pathname) : pathname;
}

/** `localizePath('es', '/about')` → `/es/about`. */
export function localizePath(language: string, pathname: string): string {
  const base = stripLocale(pathname);
  if (language === DEFAULT_LANGUAGE) return base;
  return base === '/' ? `/${language}` : `/${language}${base}`;
}
